// [목적] /u/:handle 공개 프로필 페이지 — 커버 배너 + 아바타 오버랩 + 탭(내폴리오/좋아요/Fork/뱃지) 강화
// [이유] design-align-v2: 커버 배너(그라디언트+SVG 산 실루엣), 아바타 -46px 오버랩,
//         뱃지 탭 — 실데이터만 표시(2026-09-17 mock locked 슬롯 제거), 좋아요/Fork 탭 실데이터 유지.
//         W5-2: TABS 4종 + liked/forks 실데이터 + badges 실데이터.
//         fe-mobile-live-data: ProfileMobile 에 handle prop 전달 — 이전에는 handle 무시하고 항상
//           design-mock ME 를 렌더해 어떤 /u/:handle 이든 같은 가짜 사용자가 보이던 버그를 수정.
// [사이드 이팩트] ProfileDesktop 에 cover banner Box 추가. maxWidth 패딩 계산 변경.
//                 ProfileMobile 호출부에 handle={handle} 추가.
//                 M7 P2: 팔로워/팔로잉 카운트 클릭 → /u/:handle/followers·following(FollowListPage.tsx)
//                 이동으로 교체(기존엔 팔로워 클릭이 같은 프로필로 재이동하는 버그였음). isPrivate=true 이면
//                 탭 대신 "비공개 프로필입니다" 안내 UI 렌더.
//                 FlowLine B6: 타인 프로필 헤더에 "···" 메뉴 추가 → ReportDialog(targetType='user') 재사용
//                 (components/report/ReportDialog.tsx — 다른 FE 에이전트가 생성 완료, 이 파일에서 import 만).
// [수정 시 고려사항] 커버 배너는 full-width — 컨테이너 패딩 무시하고 음수 마진 처리.
//                   뱃지 탭 locked 슬롯 재도입 시 BE 카탈로그 API 필요(mock 금지).
//                   [G1-2, 2026-09-17] usePublicProfile 실패(isError) 시 무한 ProfileSkeleton 대신
//                   InlineErrorState(재시도) 렌더 — usePublicProfile 훅에 isError/refetch 추가.

import {
  Box,
  ButtonBase,
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { TopBar } from '../components/layout/TopBar.js';
import { ErrorBoundary } from '../components/feedback/ErrorBoundary.js';
import { ProfileSkeleton, RowSkeleton } from '../components/feedback/Skeleton.js';
import { InlineErrorState } from '../components/feedback/InlineErrorState.js';
import { LoginPromptModal } from '../components/auth/LoginPromptModal.js';
import { ReportDialog } from '../components/report/ReportDialog.js';
import { useShell } from '../hooks/useShell.js';
import { useAuth } from '../hooks/useAuth.js';
import { useAuthGate } from '../hooks/useAuthGate.js';
import { usePublicProfile, useUserFolios } from '../hooks/useUserProfile.js';
import { useFollowToggle } from '../hooks/useFollowToggle.js';
import { getMyLiked, getMyForked, getMyBadgeCatalog } from '../api/user.client.js';
import type { UserFoliosPage } from '../api/user.client.js';
import { queryKeys } from '../api/queryKeys.js';
import { BadgeCollection } from '../components/badges/BadgeCollection.js';
import { BadgeMedal } from '../components/badges/BadgeMedal.js';
import { ProfileMobile } from './mobile/ProfileMobile.js';

import type { UserPublicProfile, BadgeCatalog, Folio } from '@gangwoncanvas/types';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const SOFT_LINE = c.line.soft[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
// [목적] 소형 브랜드 텍스트(11~14px) 전용 — "추가하기 →", 버튼 hover 등
// [이유] brand[500] 은 소형 텍스트에서 WCAG AA 미달(packages/ui tokens/color.ts brand[600] 주석)
const BRAND_TEXT = c.brand[600][L];

/**
 * [목적] createdAt(ISO) → "2026년 9월부터 함께하고 있어요" 문구
 * [이유] 2026-09-20 profile-visual-pass — 프로필 카드에 사람을 설명하는 정보가 이름·핸들뿐이라
 *        빈약해 보였다. 가입 시기는 서버가 이미 주는 실데이터라 추가 API 없이 채울 수 있다.
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] 파싱 실패(잘못된 문자열) 시 빈 문자열을 돌려 호출부가 줄 자체를 숨기게 한다 —
 *                   "Invalid Date" 가 화면에 노출되는 것보다 낫다.
 */
export function formatJoinedAt(createdAt: string): string {
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월부터 함께하고 있어요`;
}

// [목적] W5-2 탭 구성 — 시안(내폴리오/좋아요/Fork/뱃지) 정합 (ADR-DA5)
type TabId = 'folios' | 'liked' | 'forks' | 'badges';

const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
  { id: 'folios', label: '내 폴리오' },
  { id: 'liked', label: '좋아요' },
  { id: 'forks', label: 'Fork' },
  { id: 'badges', label: '뱃지' },
];

// ─── /profile redirect ────────────────────────────────────────────────────────

/**
 * [목적] /profile 진입 시 인증 사용자의 /u/:handle 로 redirect
 * [이유] FEED-01 — /profile 은 항상 본인 프로필 진입 경로 (handle 없는 legacy URL)
 * [사이드 이팩트] status=loading 중에는 null 반환(대기). guest 시 /login redirect
 * [수정 시 고려사항] routes.tsx 에서 /profile path 에 <ProfileRedirect /> 사용
 */
export function ProfileRedirect(): JSX.Element | null {
  const { status, user, requireAuth } = useAuth();
  if (status === 'loading') return null;
  if (status === 'guest') {
    requireAuth('/profile');
    return null;
  }
  return <Navigate to={`/u/${user!.handle}`} replace />;
}

// ─── ProfilePage ──────────────────────────────────────────────────────────────

/**
 * [목적] /u/:handle 공개 프로필 페이지 진입점 — 모바일/데스크톱 분기
 * [이유] design-align-v2: 커버 배너 + 아바타 오버랩 + 탭 강화
 * [사이드 이팩트] usePublicProfile throwOnError → ErrorBoundary 필수
 * [수정 시 고려사항] ErrorBoundary 에 isGuest + loginHref 전달 — 403/401 시 로그인 CTA 표시
 */
export function ProfilePage(): JSX.Element {
  const { handle } = useParams<{ handle: string }>();
  const { viewport } = useShell();
  const { status } = useAuth();

  return (
    <ErrorBoundary isGuest={status === 'guest'} loginHref={`/login?returnTo=/u/${handle}`}>
      {viewport.isMobile ? <ProfileMobile handle={handle} /> : <ProfileDesktop handle={handle} />}
    </ErrorBoundary>
  );
}

// ─── CoverBanner ──────────────────────────────────────────────────────────────

/**
 * [목적] 프로필 커버 배너 — 그라디언트 + SVG 산 실루엣
 * [이유] design-align-v2: 시안 커버 배너 (height 140px desktop / 100px mobile)
 * [사이드 이팩트] 컨테이너 패딩 밖으로 나가도록 음수 마진 처리
 * [수정 시 고려사항] 사용자 커스텀 커버 이미지는 후속 profile-cover-upload Spec 에서 확장.
 */
function CoverBanner({ isMobile }: { isMobile: boolean }): JSX.Element {
  return (
    <Box
      aria-hidden="true"
      sx={{
        height: isMobile ? 100 : 132,
        background: 'linear-gradient(125deg,#1A4D8F 0%,#2876D8 48%,#2BB673 128%)',
        position: 'relative',
        overflow: 'hidden',
        // [2026-09-20 profile-visual-pass] 음수 마진(mx/mt) 제거 — 커버가 컨테이너 패딩 밖으로 64px 돌출해
        // 아래 콘텐츠(x=561)와 좌우 축이 어긋나 보였다. 이제 커버와 프로필 카드가 같은 폭(896px)으로 맞고,
        // 커버 하단 모서리를 각지게 두어 바로 아래 카드와 하나의 덩어리로 이어진다.
        borderRadius: `${tokens.radius.xl}px ${tokens.radius.xl}px 0 0`,
        mb: 0,
      }}
    >
      {/* [2026-09-20] 우상단 라이트 하이라이트 — 단색 그라데이션만으로는 커버가 밋밋했다 */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 82% 12%, rgba(255,255,255,.26), transparent 46%)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      {/* SVG mountain silhouette */}
      <Box
        component="svg"
        viewBox="0 0 920 140"
        preserveAspectRatio="xMidYMax meet"
        sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '100%' }}
      >
        {/* [2026-09-20 profile-visual-pass] 산 실루엣을 어두운 남색(#1A3A6B/#12305A, opacity .5/.6)에서
            흰색 저투명도로 교체 — 진한 남색 레이어가 그라데이션 위에 무겁게 얹혀 커버 전체가 탁해 보였다.
            흰색 레이어는 아래 그라데이션 색을 살리면서 깊이감만 더한다. */}
        {/* Back mountain layer */}
        <path
          d="M0 140 L120 60 L240 100 L380 30 L520 90 L660 20 L800 80 L920 50 L920 140 Z"
          fill="#FFFFFF"
          opacity={0.22}
        />
        {/* Front mountain layer */}
        <path
          d="M0 140 L80 90 L180 115 L300 55 L440 95 L560 40 L700 85 L840 60 L920 90 L920 140 Z"
          fill="#FFFFFF"
          opacity={0.16}
        />
      </Box>
    </Box>
  );
}

// ─── ProfileDesktop ───────────────────────────────────────────────────────────

/**
 * [목적] 데스크톱 프로필 레이아웃 — 커버 배너 + 헤더(아바타 오버랩) + 탭 4종
 * [이유] design-align-v2: 커버 배너, 아바타 -46px 오버랩, 뱃지 탭 디자인 강화.
 *         W5-2: TABS 4종. fe-be-gap-tier1 T8/T14: liked/forks·뱃지 실데이터 연결.
 * [사이드 이팩트] 커버 배너 높이만큼 상단 패딩 조정.
 * [수정 시 고려사항] isMe=true 이면 팔로우 버튼 미표시 + 편집/새폴리오/설정 버튼 3개 표시.
 */
function ProfileDesktop({ handle }: { handle: string | undefined }): JSX.Element {
  const navigate = useNavigate();
  const { viewport, openSidebar } = useShell();
  // [2026-09-21 notif-badge-deeplink] ?tab=badges 등 쿼리스트링으로 초기 탭 진입 지원 —
  // 알림(뱃지 획득 등)에서 프로필 특정 탭으로 바로 이동시키기 위함. 유효하지 않은 값이면 기본값(folios) 유지.
  const [searchParams] = useSearchParams();
  const initialTabParam = searchParams.get('tab');
  const [tab, setTab] = useState<TabId>(
    TABS.some((t) => t.id === initialTabParam) ? (initialTabParam as TabId) : 'folios',
  );

  const { profile, isLoading, isError, refetch } = usePublicProfile(handle);
  const { folios, isLoading: foliosLoading } = useUserFolios(handle);
  const isMe = profile?.isMe === true;

  // liked 무한스크롤 — isMe 본인일 때만 활성화 (T8 ADR-T1-2)
  const {
    data: likedData,
    fetchNextPage: fetchNextLiked,
    hasNextPage: hasNextLiked,
    isFetchingNextPage: isFetchingNextLiked,
    isLoading: likedLoading,
  } = useInfiniteQuery({
    queryKey: queryKeys.user.liked('me'),
    queryFn: ({ pageParam }) => getMyLiked({ cursor: pageParam as string | undefined, limit: 20 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: isMe,
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });

  // forked 무한스크롤 — isMe 본인일 때만 활성화 (T8 ADR-T1-1·ADR-T1-2)
  const {
    data: forkedData,
    fetchNextPage: fetchNextForked,
    hasNextPage: hasNextForked,
    isFetchingNextPage: isFetchingNextForked,
    isLoading: forkedLoading,
  } = useInfiniteQuery({
    queryKey: queryKeys.user.forked('me'),
    queryFn: ({ pageParam }) => getMyForked({ cursor: pageParam as string | undefined, limit: 20 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: isMe,
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });

  // 뱃지 도감(획득 + 미획득 + 진행률) — isMe 본인일 때만 활성화 (T14 ADR-T1-5, 2026-09-21 badge-catalog)
  // [이유] 보유 목록(GET /users/me/badges)만으로는 잠금 슬롯을 그릴 수 없다. 카탈로그 응답 1건에
  //        earned/locked/진행률이 모두 들어 있어 별도 호출 없이 탭 + 뱃지 카운트를 동시에 채운다.
  const { data: badgeCatalog, isLoading: badgesLoading } = useQuery<BadgeCatalog>({
    queryKey: queryKeys.user.badgeCatalog('me'),
    queryFn: () => getMyBadgeCatalog(),
    enabled: isMe,
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
  });

  const likedItems = likedData?.pages.flatMap((p) => p.items) ?? [];
  const forkedItems = forkedData?.pages.flatMap((p) => p.items) ?? [];
  const badgeEarnedCount = badgeCatalog?.earnedCount ?? 0;

  return (
    <>
      <TopBar title="프로필" mobile={viewport.isMobile} onBurger={openSidebar} />

      {/* 컨테이너 래퍼 — 커버 배너 + 콘텐츠 */}
      <Box
        sx={{
          p: {
            xs: `${tokens.spacing[6]}px ${tokens.spacing[4]}px 44px`,
            md: `${tokens.spacing[6]}px ${tokens.spacing[8]}px 44px`,
          },
          maxWidth: 960,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* 커버 배너 — 아래 프로필 카드와 이어붙는 상단 면 (2026-09-20: 음수 마진 제거) */}
        <CoverBanner isMobile={viewport.isMobile} />

        {/* 프로필 헤더 — [G1-2] isError 시 무한 스켈레톤 대신 InlineErrorState(재시도) 렌더 */}
        {isError ? (
          <Box sx={{ pt: '60px' }}>
            <InlineErrorState message="프로필을 불러오지 못했습니다." onRetry={refetch} />
          </Box>
        ) : isLoading || !profile ? (
          // 배너 높이 보상 spacer + skeleton
          <Box sx={{ pt: '60px' }}>
            <ProfileSkeleton />
          </Box>
        ) : (
          <ProfileHeader
            profile={profile}
            badgeCount={isMe ? badgeEarnedCount : undefined}
            onNavigateEdit={() => navigate('/settings/profile')}
            onNavigateChat={() => navigate('/chat')}
            onNavigateSettings={() => navigate('/settings')}
            onNavigateFollowers={(h) => navigate(`/u/${h}/followers`)}
            onNavigateFollowing={(h) => navigate(`/u/${h}/following`)}
          />
        )}

        {/* M7 P2: 비공개 프로필(isPrivate=true) — 탭 대신 안내 UI */}
        {profile?.isPrivate ? (
          <Box sx={{ textAlign: 'center', p: '60px 20px', color: INK_LOW }}>
            <Box aria-hidden="true" sx={{ fontSize: 36, mb: '12px', opacity: 0.4 }}>
              🔒
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK_HI, mb: '6px' }}>
              비공개 프로필입니다
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: INK_MID }}>
              @{profile.handle}님이 프로필을 비공개로 설정했습니다.
            </Typography>
          </Box>
        ) : (
          <>
            {/* 탭 네비게이션 */}
            {/* [목적] 프로필 탭 — 밑줄 탭 → pill 세그먼트 + 숫자 배지
                [이유] 2026-09-20 profile-visual-pass — 12px 텍스트 + 2px 밑줄은 데스크톱에서 클릭 타겟이
                       지나치게 작았고, 개수 숫자도 라벨 옆에 작게 떠 있어 읽히지 않았다.
                       발견 필터칩·알림 탭과 같은 pill 언어로 통일한다.
                [사이드 이팩트] role/aria-selected/aria-controls/aria-label 은 기존 그대로 — 접근성·테스트 호환. */}
            <Box
              role="tablist"
              aria-label="프로필 탭"
              sx={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                mb: `${tokens.spacing[5]}px`,
              }}
            >
              {TABS.map((t) => {
                const active = tab === t.id;
                const count =
                  t.id === 'folios'
                    ? (profile?.folioCount ?? 0)
                    : t.id === 'liked'
                      ? likedItems.length
                      : t.id === 'forks'
                        ? forkedItems.length
                        : badgeEarnedCount;
                const hasSuffix =
                  (t.id === 'liked' && hasNextLiked) || (t.id === 'forks' && hasNextForked);
                return (
                  <ButtonBase
                    key={t.id}
                    role="tab"
                    aria-selected={active}
                    aria-controls={`tabpanel-${t.id}`}
                    id={`tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    aria-label={`${t.label} ${count}${hasSuffix ? '+' : ''}개`}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      pl: '18px',
                      pr: '8px',
                      height: 40,
                      borderRadius: 99,
                      fontSize: 13,
                      fontWeight: active ? 700 : 600,
                      bgcolor: active ? INK_HI : PAPER0,
                      color: active ? PAPER0 : INK_MID,
                      border: `1px solid ${active ? INK_HI : BORDER}`,
                      boxShadow: active ? '0 3px 10px rgba(41,38,27,.22)' : 'none',
                      transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                      '&:hover': active ? {} : { borderColor: BRAND, color: BRAND_TEXT },
                      '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                    }}
                  >
                    {t.label}
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 22,
                        height: 22,
                        px: '6px',
                        borderRadius: 99,
                        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                        fontSize: 11,
                        fontWeight: 800,
                        // 선택 탭은 어두운 면 위이므로 반투명 흰 배경으로 반전
                        bgcolor: active ? 'rgba(255,255,255,.22)' : SOFT_LINE,
                        color: active ? PAPER0 : INK_MID,
                      }}
                    >
                      {count}
                      {hasSuffix ? '+' : ''}
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>

            {/* 탭 콘텐츠 */}
            <Box id={`tabpanel-${tab}`} role="tabpanel" aria-labelledby={`tab-${tab}`}>
              {tab === 'folios' && (
                <FoliosTab
                  folios={folios}
                  isLoading={foliosLoading}
                  isMe={isMe}
                  onOpen={(id) => navigate(`/folio/${id}`)}
                  onCreate={() => navigate('/new-folio')}
                  onExplore={() => navigate('/explore')}
                />
              )}
              {tab === 'liked' && (
                <FolioListTab
                  items={likedItems}
                  isLoading={likedLoading}
                  isMe={isMe}
                  emptyIcon="♡"
                  emptyLabel="좋아요한 폴리오"
                  hasNextPage={hasNextLiked ?? false}
                  isFetchingNextPage={isFetchingNextLiked}
                  onFetchNextPage={fetchNextLiked}
                  onOpen={(id) => navigate(`/folio/${id}`)}
                />
              )}
              {tab === 'forks' && (
                <FolioListTab
                  items={forkedItems}
                  isLoading={forkedLoading}
                  isMe={isMe}
                  emptyIcon="⑂"
                  emptyLabel="내가 Fork한 폴리오"
                  hasNextPage={hasNextForked ?? false}
                  isFetchingNextPage={isFetchingNextForked}
                  onFetchNextPage={fetchNextForked}
                  onOpen={(id) => navigate(`/folio/${id}`)}
                />
              )}
              {tab === 'badges' && (
                <BadgesTab catalog={badgeCatalog} isLoading={badgesLoading} isMe={isMe} />
              )}
            </Box>

            {/* [목적] 시작 가이드 — 빈 본인 프로필에서 페이지 하단 여백을 다음 행동으로 전환
                [이유] 2026-09-20 profile-visual-pass — 폴리오 0건이면 탭 아래가 뷰포트 절반 이상 통째로 비어
                       서비스 자체가 빈 것처럼 읽혔다. 신규 사용자에게 실제로 필요한 동선 3개를 놓는다.
                [사이드 이팩트] 본인(isMe) · 폴리오 0건 · folios 로딩 완료 세 조건을 모두 만족할 때만 노출 —
                               폴리오가 생기면 자연스럽게 사라진다.
                [수정 시 고려사항] 탭과 무관하게 페이지 하단에 고정 — 탭별로 다르게 넣지 말 것(맥락이 흘들린다). */}
            {isMe && !foliosLoading && (folios?.items.length ?? 0) === 0 && <ProfileStarterGuide />}
          </>
        )}
      </Box>
    </>
  );
}

// ─── ProfileHeader ────────────────────────────────────────────────────────────

/**
 * [목적] 프로필 헤더 — 아바타(-46px 커버 오버랩) + 이름/handle/bio + 통계 + 액션 버튼
 * [이유] design-align-v2: 아바타를 커버 배너와 겹치게 margin-top:-46px, border:4px solid #fff, boxShadow 추가.
 * [사이드 이팩트] 커버 배너 높이(140px) - 아바타 오버랩(-46px) = 상단 여백 94px. 헤더 mb 축소.
 * [수정 시 고려사항] 아바타 border 색상 토큰 없음 — PAPER0 직접 사용 허용(디자인 토큰 예외 케이스).
 */
function ProfileHeader({
  profile,
  badgeCount,
  onNavigateEdit,
  onNavigateChat,
  onNavigateSettings,
  onNavigateFollowers,
  onNavigateFollowing,
}: {
  profile: UserPublicProfile;
  /**
   * [목적] 통계 블록의 "뱃지" 칸 값 — undefined 면 칸 자체를 렌더하지 않는다
   * [이유] 뱃지 API 는 enabled:isMe 로 본인일 때만 호출된다 — 타인 프로필에서 0 을 보여주면
   *        "뱃지가 없는 사람"으로 오독되는 거짓말이 된다.
   */
  badgeCount?: number;
  onNavigateEdit: () => void;
  onNavigateChat: () => void;
  onNavigateSettings: () => void;
  onNavigateFollowers: (handle: string) => void;
  onNavigateFollowing: (handle: string) => void;
}): JSX.Element {
  const { toggle, isPending } = useFollowToggle(profile.handle);

  // FlowLine B6: 타인 프로필 "···" 메뉴 → 신고(ReportDialog targetType='user' 재사용, CommentSection.tsx 관례)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const reportGate = useAuthGate('report.create');

  return (
    // [목적] 프로필 카드 — 커버와 이어붙는 흰 면 + grid(아바타 | 본문) + 통계 행
    // [이유] 2026-09-20 profile-visual-pass: 기존엔 커버와 본문이 서로 다른 면이라 "프로필 카드"라는
    //        덩어리가 안 생겼고, 이름/핸들이 왼쪽에만 있어 오른쪽 700px가 통째로 비었다.
    //        grid 로 바꿔 1행은 (아바타 | 이름·소개·메타 + 우상단 액션), 2행은 카드 전체 폭 통계 블록으로 둘다.
    // [사이드 이팩트] 통계가 아바타 오른쪽에서 시작하던 것 → 카드 좌우 끝에 정렬된다(gridColumn '1 / -1').
    // [수정 시 고려사항] 아바타 mt 음수값(-52px)은 CoverBanner 높이(132px)와 쌍 — 한쪽만 바꾸면
    //                   아바타가 커버 밖으로 튀거나 묻힌다.
    <Box
      component="section"
      aria-label="프로필 정보"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' },
        columnGap: '22px',
        rowGap: 0,
        alignItems: 'start',
        mb: `${tokens.spacing[6]}px`,
        bgcolor: PAPER0,
        border: `1px solid ${BORDER}`,
        borderTop: 'none',
        borderRadius: `0 0 ${tokens.radius.xl}px ${tokens.radius.xl}px`,
        p: { xs: '0 20px 22px', md: '0 30px 26px' },
      }}
    >
      {/* 아바타 — 커버 배너 오버랩 (2026-09-20: 커버 132px 기준 -52px) */}
      {/* [수정: 2026-09-20 레이아웃 QA] CoverBanner 가 position:'relative' 를 가지고 있어서,
          static 인 이 아바타가 DOM 순서상 뒤에 있어도 CSS 기본 쌍임 규칙(positioned 요소가 static 보다 항상 위)에
          따라 배너에 46px 만큼 가려져 율이 잘려 보이던 버그 수정 — position:'relative' + zIndex:1 로
          배너보다 위에 그려지도록 명시. */}
      <Box
        aria-hidden={Boolean(profile.avatarUrl)}
        sx={{
          gridColumn: { md: 1 },
          gridRow: { md: 1 },
          width: 104,
          height: 104,
          borderRadius: '50%',
          bgcolor: BRAND,
          color: PAPER0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 40,
          fontWeight: 800,
          flexShrink: 0,
          // 커버 배너 오버랩 (커버 132px 기준)
          mt: '-52px',
          border: `5px solid ${PAPER0}`,
          boxShadow: '0 12px 28px rgba(41,38,27,.18)',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {profile.avatarUrl ? (
          <Box
            component="img"
            src={profile.avatarUrl}
            alt={`${profile.displayName} 프로필 사진`}
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          profile.displayName.charAt(0).toUpperCase()
        )}
      </Box>

      {/* [수정: 2026-09-20 레이아웃 QA] 텍스트 컴럼이 플렉스 행 톱(alignItems:'flex-start')을 그대로 따르면서
          아바타와 달리 보정 margin 이 없어 이름 텍스트가 커버 배너 하단과 정확히 같은 y 좌표에서 시작해
          배너에 바로 붙어 보이던 문제 수정 — pt 로 약간의 여백을 추가. */}
      <Box
        sx={{
          gridColumn: { md: 2 },
          gridRow: { md: 1 },
          minWidth: 0,
          pt: '16px',
          position: 'relative',
        }}
      >
        {/* 이름 + handle */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '10px',
            flexWrap: 'wrap',
            // 우상단 절대배치된 액션 버튼과 글자가 겹치지 않도록 확보하는 여유 폭
            pr: { md: '320px' },
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 22, md: 28 },
              fontWeight: 800,
              color: INK_HI,
              letterSpacing: '-0.7px',
              lineHeight: 1.25,
              m: 0,
            }}
          >
            {profile.displayName}
          </Typography>
          {/* [2026-09-20] 핸들을 모노 pill 로 — 이름 28px 옆에 평문으로 붙으면 위계가 애매했다 */}
          <Typography
            component="span"
            sx={{
              fontSize: 13,
              color: INK_LOW,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              bgcolor: SOFT_LINE,
              px: '9px',
              py: '3px',
              borderRadius: 99,
            }}
          >
            @{profile.handle}
          </Typography>
        </Box>

        {/* [목적] 자기소개 — 값이 있으면 그대로, 본인이고 비어있으면 작성 유도
            [이유] 2026-09-20: bio 가 없으면 줄 자체가 사라져 이름 밑이 허전했고, 채울 경로도 없었다.
            [사이드 이팩트] 타인 프로필에서 bio 가 비어있으면 아무것도 렌더하지 않는다(유도 문구는 본인 전용). */}
        {profile.bio ? (
          <Typography
            sx={{
              fontSize: 13.5,
              color: INK_MID,
              lineHeight: 1.6,
              m: 0,
              mt: '9px',
              maxWidth: 560,
            }}
          >
            {profile.bio}
          </Typography>
        ) : (
          profile.isMe && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
                mt: '9px',
                fontSize: 13.5,
                color: INK_LOW,
              }}
            >
              <Box component="span" sx={{ fontStyle: 'italic' }}>
                자기소개가 아직 없어요.
              </Box>
              <ButtonBase
                onClick={onNavigateEdit}
                aria-label="자기소개 추가하기"
                sx={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: BRAND_TEXT,
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                추가하기 →
              </ButtonBase>
            </Box>
          )
        )}

        {/* [목적] 가입 시기 — 프로필 맥락 정보
            [이유] 2026-09-20: 이름·핸들 외에 사람을 설명하는 정보가 전혀 없어 카드가 빈약해 보였다.
            [수정 시 고려사항] createdAt 은 UserPublicProfile 의 ISO 문자열 — 실데이터만 쓴다(임의 값 추가 금지). */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            mt: '11px',
            fontSize: 11.5,
            color: INK_LOW,
          }}
        >
          <Box component="span" aria-hidden="true">
            🗓
          </Box>
          <Box component="span">{formatJoinedAt(profile.createdAt)}</Box>
        </Box>

        {/* 액션 버튼 — 카드 우상단 절대배치(비어 있던 오른쪽 공간 활용) */}
        <Stack
          direction="row"
          spacing="8px"
          sx={{
            alignItems: 'center',
            mt: { xs: '16px', md: 0 },
            position: { md: 'absolute' },
            right: { md: 0 },
            top: { md: '14px' },
          }}
        >
          {profile.isMe ? (
            <>
              <ButtonBase
                onClick={onNavigateChat}
                aria-label="새 폴리오 만들기"
                sx={{
                  bgcolor: BRAND,
                  color: PAPER0,
                  borderRadius: 99,
                  px: '18px',
                  height: 40,
                  fontSize: 12.5,
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(31,111,235,.26)',
                  '&:hover': { opacity: 0.9 },
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                + 새 폴리오
              </ButtonBase>
              <ButtonBase
                onClick={onNavigateEdit}
                aria-label="프로필 편집"
                sx={{
                  bgcolor: PAPER0,
                  color: INK_HI,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 99,
                  px: '18px',
                  height: 40,
                  fontSize: 12.5,
                  fontWeight: 700,
                  transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                  '&:hover': { borderColor: BRAND, color: BRAND_TEXT },
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                프로필 편집
              </ButtonBase>
              {/* [2026-09-20] ⚙ 버튼 — pill 두 개 옆에서 혼자 작은 정사각이라 떠 보였다.
                  같은 40px 높이의 원형 아이콘 버튼으로 통일. */}
              <ButtonBase
                onClick={onNavigateSettings}
                aria-label="설정"
                sx={{
                  bgcolor: PAPER0,
                  color: INK_MID,
                  border: `1px solid ${BORDER}`,
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  fontSize: 15,
                  transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                  '&:hover': { borderColor: BRAND, color: BRAND_TEXT },
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                ⚙
              </ButtonBase>
            </>
          ) : (
            <>
              <ButtonBase
                onClick={() => toggle(profile.isFollowing)}
                disabled={isPending}
                aria-label={profile.isFollowing ? '팔로우 취소' : `${profile.displayName} 팔로우`}
                aria-pressed={profile.isFollowing}
                sx={{
                  bgcolor: profile.isFollowing ? PAPER0 : BRAND,
                  color: profile.isFollowing ? INK_HI : PAPER0,
                  border: `1px solid ${profile.isFollowing ? BORDER : BRAND}`,
                  borderRadius: `${tokens.radius.s}px`,
                  px: '16px',
                  py: '8px',
                  fontSize: 11.5,
                  fontWeight: 700,
                  gap: '6px',
                  opacity: isPending ? 0.6 : 1,
                  transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                {isPending ? (
                  <CircularProgress size={12} color="inherit" aria-label="처리 중" />
                ) : profile.isFollowing ? (
                  '팔로우 중'
                ) : (
                  '팔로우'
                )}
              </ButtonBase>

              {/* FlowLine B6: "···" 메뉴 → 신고 */}
              <ButtonBase
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                aria-label="더 보기"
                aria-haspopup="menu"
                aria-expanded={Boolean(menuAnchor)}
                sx={{
                  bgcolor: PAPER0,
                  color: INK_HI,
                  border: `1px solid ${BORDER}`,
                  borderRadius: `${tokens.radius.s}px`,
                  px: '10px',
                  py: '8px',
                  fontSize: 14,
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                ···
              </ButtonBase>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    reportGate.guard(() => setReportOpen(true));
                  }}
                >
                  신고
                </MenuItem>
              </Menu>
              <LoginPromptModal
                open={reportGate.isPromptOpen}
                intent={reportGate.pendingIntent}
                onClose={reportGate.closePrompt}
              />
              {reportOpen && (
                <ReportDialog
                  targetType="user"
                  targetId={profile.id}
                  open
                  onClose={() => setReportOpen(false)}
                />
              )}
            </>
          )}
        </Stack>
      </Box>

      {/* [목적] 통계 블록 — 카드 전체 폭(grid 2행)
          [이유] 2026-09-20: 기존 통계는 11.5px 텍스트 한 줄("0 폴리오 0 팔로워 0 팔로잉")으로 뭉개져 있어
                 프로필에서 가장 먼저 읽힐 숫자가 가장 안 보였다. 아바타 오른쪽에서 시작해 카드 폭과
                 좌측 정렬도 안 맞았다. gridColumn '1 / -1' 로 카드 좌우 끝에 맞추고 숫자를 21px 모노로 올렸다.
          [사이드 이팩트] 팔로워/팔로잉 칸은 기존처럼 클릭 가능(목록 페이지로 이동) — 폴리오/뷰지 칸은 비대화.
          [수정 시 고려사항] 뷰지 칸은 badgeCount 가 주어졌을 때(= 본인 프로필)만 렌더한다 — 타인 프로필은
                            뷰지 API 자체를 호출하지 않아(enabled: isMe) 0 을 보여주면 거짓말이 된다. */}
      <Box
        sx={{
          gridColumn: { md: '1 / -1' },
          gridRow: { md: 2 },
          display: 'flex',
          mt: '22px',
          border: `1px solid ${BORDER}`,
          borderRadius: `${tokens.radius.l}px`,
          overflow: 'hidden',
          bgcolor: PAPER1,
        }}
      >
        <ProfileStat value={profile.folioCount} label="폴리오" />
        <ProfileStat
          divider
          value={profile.followerCount}
          label="팔로워"
          onClick={() => onNavigateFollowers(profile.handle)}
          ariaLabel={`팔로워 ${profile.followerCount}명`}
        />
        <ProfileStat
          divider
          value={profile.followingCount}
          label="팔로잉"
          onClick={() => onNavigateFollowing(profile.handle)}
          ariaLabel={`팔로잉 ${profile.followingCount}명`}
        />
        {badgeCount !== undefined && <ProfileStat divider value={badgeCount} label="뱃지" />}
      </Box>
    </Box>
  );
}

// ─── ProfileStat ──────────────────────────────────────────────────────────────

/**
 * [목적] 프로필 통계 한 칸 — 큰 모노 숫자 + 라벨
 * [이유] 2026-09-20 profile-visual-pass — 네 칸이 동일한 마크업을 반복하므로 컴포넌트로 묶어
 *        구분선/패딩/타이포가 칸마다 어긋나는 것을 구조적으로 막는다.
 * [사이드 이팩트] onClick 이 있으면 ButtonBase(실제 <button>), 없으면 <div> 로 렌더 —
 *                 클릭할 수 없는 칸에 버튼 시맨틱을 주지 않는다(스크린리더 오안내 방지).
 * [수정 시 고려사항] 좌측 구분선은 divider prop 으로 명시적으로 받는다 — CSS `:first-of-type` 을 쓰면
 *                   클릭 가능한 칸(<button>)과 아닌 칸(<div>)의 타입이 달라 "각 타입의 첫 번째"가 각각
 *                   구분선을 잃는다(실제로 폴리오~팔로워 사이 구분선이 사라졌던 버그).
 */
function ProfileStat({
  value,
  label,
  divider,
  onClick,
  ariaLabel,
}: {
  value: number;
  label: string;
  divider?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
}): JSX.Element {
  const inner = (
    <>
      <Box
        sx={{
          fontSize: 21,
          fontWeight: 800,
          color: INK_HI,
          fontFamily: 'JetBrains Mono, ui-monospace, monospace',
          lineHeight: 1.1,
        }}
      >
        {value}
      </Box>
      <Box sx={{ fontSize: 11, color: INK_LOW, mt: '5px' }}>{label}</Box>
    </>
  );

  const baseSx = {
    flex: 1,
    minWidth: 0,
    p: '14px 10px',
    textAlign: 'center' as const,
    display: 'block',
    borderLeft: divider ? `1px solid ${BORDER}` : 'none',
  };

  if (!onClick) return <Box sx={baseSx}>{inner}</Box>;

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      sx={{
        ...baseSx,
        transition: `background ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
        '&:hover': { bgcolor: PAPER2 },
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: -2 },
      }}
    >
      <Box>{inner}</Box>
    </ButtonBase>
  );
}

// ─── FoliosTab ────────────────────────────────────────────────────────────────

/**
 * [목적] 프로필 폴리오 탭 콘텐츠 — 폴리오 행 목록
 * [이유] FEED-01 ProfilePage 폴리오 탭 실데이터 렌더
 * [사이드 이팩트] foliosLoading 중 RowSkeleton 표시
 * [수정 시 고려사항] 무한스크롤 미적용 — 첫 페이지 20개 표시 (후속 확장 가능)
 */
function FoliosTab({
  folios,
  isLoading,
  isMe,
  onOpen,
  onCreate,
  onExplore,
}: {
  folios: UserFoliosPage | undefined;
  isLoading: boolean;
  isMe: boolean;
  onOpen: (id: string) => void;
  onCreate: () => void;
  onExplore: () => void;
}): JSX.Element {
  if (isLoading) {
    return (
      <Stack spacing="8px">
        {[...Array(4)].map((_, i) => (
          <RowSkeleton key={i} />
        ))}
      </Stack>
    );
  }

  if (!folios || folios.items.length === 0) {
    // [목적] 폴리오 0건 빈 상태
    // [이유] 2026-09-20 profile-visual-pass — 기존은 ❑ 글리프 + "아직 폴리오가 없습니다." 한 줄이
    //        180px 블록에 떠 있고 그 아래 700px 가 통째로 비어 화면이 고장난 것처럼 보였다.
    // [사이드 이팩트] CTA 는 본인(isMe) 일 때만 — 타인 프로필에서 "첫 폴리오 만들기"는 문맥에 맞지 않는다.
    return (
      <ProfileEmptyCard
        emoji="🗺"
        title={isMe ? '아직 만든 폴리오가 없어요' : '아직 공개된 폴리오가 없어요'}
        description={
          isMe
            ? '가고 싶은 곳을 말하면 강원캔버스가 일정을 그려드려요.'
            : '이 사용자가 폴리오를 공개하면 여기에 표시돼요.'
        }
        actions={
          isMe
            ? [
                { label: '+ 첫 폴리오 만들기', onClick: onCreate, primary: true },
                { label: '다른 여행 둘러보기', onClick: onExplore },
              ]
            : []
        }
      />
    );
  }

  return (
    <Stack spacing="8px">
      {folios.items.map((f) => (
        <ButtonBase
          key={f.id}
          onClick={() => onOpen(f.id)}
          aria-label={`폴리오 ${f.title} 열기`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            p: '12px 14px',
            bgcolor: PAPER0,
            border: `1px solid ${BORDER}`,
            borderRadius: `${tokens.radius.m}px`,
            width: '100%',
            textAlign: 'left',
            '&:hover': { bgcolor: PAPER2 },
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
          }}
        >
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: `${tokens.radius.m}px`,
              bgcolor: PAPER2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {f.coverImageUrl ? (
              <Box
                component="img"
                src={f.coverImageUrl}
                alt={f.title}
                loading="lazy"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box component="span" aria-hidden="true" sx={{ fontSize: 22 }}>
                🗺
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mb: '3px' }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: INK_HI,
                  letterSpacing: '-0.2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {f.title}
              </Typography>
              {f.visibility === 'private' && (
                <Box
                  component="span"
                  aria-label="비공개"
                  sx={{
                    fontSize: 9.5,
                    bgcolor: PAPER2,
                    color: INK_MID,
                    borderRadius: `${tokens.radius.s}px`,
                    px: '6px',
                    py: '2px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  비공개
                </Box>
              )}
            </Box>
            <Typography
              sx={{
                fontSize: 10.5,
                color: INK_LOW,
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              }}
            >
              ♥ {f.likeCount}
            </Typography>
          </Box>
          <Box component="span" aria-hidden="true" sx={{ fontSize: 14, color: INK_LOW }}>
            ›
          </Box>
        </ButtonBase>
      ))}
    </Stack>
  );
}

// ─── FolioListTab ─────────────────────────────────────────────────────────────

/**
 * [목적] liked/forks 탭 실데이터 목록 — 무한스크롤 + 빈상태 graceful (fe-be-gap-tier1 T8)
 * [이유] GET /users/me/liked·/forked 실데이터 연결. isMe=false(타인 프로필)시 API 미제공 → 빈상태.
 * [사이드 이팩트] 기존 FoliosTab 과 동일 RowItem 스타일로 렌더.
 * [수정 시 고려사항] 타인 프로필의 liked/forks API 신설 시 isMe 조건 제거 가능.
 */
function FolioListTab({
  items,
  isLoading,
  isMe,
  emptyIcon,
  emptyLabel,
  hasNextPage,
  isFetchingNextPage,
  onFetchNextPage,
  onOpen,
}: {
  items: Folio[];
  isLoading: boolean;
  isMe: boolean;
  emptyIcon: string;
  emptyLabel: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onFetchNextPage: () => void;
  onOpen: (id: string) => void;
}): JSX.Element {
  if (isLoading) {
    return (
      <Stack spacing="8px">
        {[...Array(3)].map((_, i) => (
          <RowSkeleton key={i} />
        ))}
      </Stack>
    );
  }

  if (!isMe) {
    return (
      <Box sx={{ textAlign: 'center', p: '60px 20px', color: INK_LOW }}>
        <Box aria-hidden="true" sx={{ fontSize: 36, mb: '12px', opacity: 0.4 }}>
          {emptyIcon}
        </Box>
        <Typography sx={{ fontSize: 13, color: INK_MID }}>
          {emptyLabel} 목록은 본인 프로필에서만 확인 가능합니다.
        </Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: '60px 20px', color: INK_LOW }}>
        <Box aria-hidden="true" sx={{ fontSize: 36, mb: '12px', opacity: 0.4 }}>
          {emptyIcon}
        </Box>
        <Typography sx={{ fontSize: 13, color: INK_MID }}>아직 {emptyLabel}가 없습니다.</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing="8px">
      {items.map((f) => (
        <ButtonBase
          key={f.id}
          onClick={() => onOpen(f.id)}
          aria-label={`폴리오 ${f.title} 열기`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            p: '12px 14px',
            bgcolor: PAPER2,
            border: `1px solid ${BORDER}`,
            borderRadius: `${tokens.radius.m}px`,
            width: '100%',
            textAlign: 'left',
            '&:hover': { bgcolor: PAPER2 },
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
          }}
        >
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: `${tokens.radius.m}px`,
              bgcolor: PAPER2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {f.coverImageUrl ? (
              <Box
                component="img"
                src={f.coverImageUrl}
                alt={f.title}
                loading="lazy"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box component="span" aria-hidden="true" sx={{ fontSize: 22 }}>
                🗺
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: INK_HI,
                letterSpacing: '-0.2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: '3px',
              }}
            >
              {f.title}
            </Typography>
            <Typography
              sx={{
                fontSize: 10.5,
                color: INK_LOW,
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              }}
            >
              ♥ {f.likeCount}
            </Typography>
          </Box>
          <Box component="span" aria-hidden="true" sx={{ fontSize: 14, color: INK_LOW }}>
            ›
          </Box>
        </ButtonBase>
      ))}
      {hasNextPage && (
        <Box sx={{ textAlign: 'center', pt: '8px' }}>
          <ButtonBase
            onClick={onFetchNextPage}
            disabled={isFetchingNextPage}
            aria-label="더 불러오기"
            sx={{
              px: '20px',
              py: '8px',
              fontSize: 12,
              color: BRAND,
              border: `1px solid ${BORDER}`,
              borderRadius: `${tokens.radius.m}px`,
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            {isFetchingNextPage ? (
              <CircularProgress size={14} color="inherit" aria-label="불러오는 중" />
            ) : (
              '더 보기'
            )}
          </ButtonBase>
        </Box>
      )}
    </Stack>
  );
}

// ─── BadgesTab ────────────────────────────────────────────────────────────────

/**
 * [목적] 뱃지 탭 — 전체 도감(획득 + 미획득 회색 슬롯 + 진행률)
 * [이유] 2026-09-21 badge-catalog: 기존 탭은 보유 배지만 3열로 렌더해, 배지가 0개인 사용자에게는
 *         "아직 획득한 뱃지가 없습니다" 한 줄만 보였다(무엇을 하면 얻는지 알 수 없음).
 *         잠금 슬롯 데이터는 BE 카탈로그(GET /users/me/badges/catalog)에서만 받는다 — FE mock 금지
 *         (2026-09-17 ADR-T1-5 위반 재발 방지: 가짜 '보유한 뱃지' 표시 버그).
 * [사이드 이팩트] 카드 렌더는 components/badges/BadgeCollection.tsx 공용 — 모바일 프로필과 동일 UI.
 * [수정 시 고려사항] 라벨/조건 문구는 BE(badge-definitions.ts) 소유. 여기서 문구를 덧붙이지 말 것.
 */
function BadgesTab({
  catalog,
  isLoading,
  isMe,
}: {
  catalog: BadgeCatalog | undefined;
  isLoading: boolean;
  isMe: boolean;
}): JSX.Element {
  if (!isMe) {
    return (
      <ProfileEmptyCard
        emoji={<BadgeMedal kind="first_folio" tier="silver" locked size={52} />}
        title="뱃지는 본인만 볼 수 있어요"
        description="다른 여행자의 뱃지 도감은 공개되지 않습니다."
        actions={[]}
      />
    );
  }

  if (isLoading || !catalog) {
    return (
      <Stack spacing="8px">
        {[...Array(3)].map((_, i) => (
          <RowSkeleton key={i} />
        ))}
      </Stack>
    );
  }

  return (
    <BadgeCollection
      items={catalog.items}
      earnedCount={catalog.earnedCount}
      totalCount={catalog.totalCount}
    />
  );
}

// ─── ProfileEmptyCard ─────────────────────────────────────────────────────────

/**
 * [목적] 프로필 탭 공용 빈 상태 카드 — 큰 아이콘 + 제목 + 설명 + 선택적 CTA
 * [이유] 2026-09-20 profile-visual-pass — 기존 빈 상태는 작은 글리프 + 한 줄 문구라
 *        "왜 비어 있고 무엇을 하면 되는지"를 전혀 알려주지 않았고, 넓은 데스크톱 화면에서
 *        고장난 페이지처럼 보였다.
 * [사이드 이팩트] actions 가 빈 배열이면 버튼 영역 자체를 렌더하지 않는다(타인 프로필 대응).
 * [수정 시 고려사항] 알림 페이지 빈 상태(NotificationsPage.EmptyNotifications)와 톤을 맞춘 형태다 —
 *                   한쪽만 바꾸면 화면 간 언어가 갈라지므로 둘을 함께 볼 것.
 */
function ProfileEmptyCard({
  emoji,
  title,
  description,
  actions,
}: {
  // [2026-09-21] 이모지 문자열 외에 노드(뱃지 메달 등)도 받도록 확장 — 이모지 의존을 줄이는 방향
  //              (components/icons/LineIcons.tsx 헤더의 "이모지 → 라인 아이콘" 정리와 같은 맥락)
  emoji: ReactNode;
  title: string;
  description: string;
  actions: ReadonlyArray<{ label: string; onClick: () => void; primary?: boolean }>;
}): JSX.Element {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: PAPER0,
        border: `1px solid ${BORDER}`,
        borderRadius: `${tokens.radius.l}px`,
        p: { xs: '36px 20px 32px', md: '44px 32px 40px' },
        textAlign: 'center',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(160deg, rgba(31,111,235,.05), rgba(43,182,115,.04) 50%, transparent 75%)',
          pointerEvents: 'none',
        }}
      />
      <Box sx={{ position: 'relative' }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 72,
            height: 72,
            borderRadius: `${tokens.radius.l}px`,
            mx: 'auto',
            mb: '18px',
            // 살짝 기울여 "카드가 쌓이는 자리"라는 느낌을 준다
            transform: 'rotate(-6deg)',
            background: 'linear-gradient(145deg,#E3EDFB,#EAF3EC)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 32,
          }}
        >
          {emoji}
        </Box>
        <Typography
          sx={{ fontSize: 18, fontWeight: 800, color: INK_HI, letterSpacing: '-0.4px', mb: '7px' }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: INK_LOW,
            lineHeight: 1.6,
            maxWidth: 360,
            mx: 'auto',
            mb: actions.length > 0 ? '20px' : 0,
          }}
        >
          {description}
        </Typography>
        {actions.length > 0 && (
          <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {actions.map((a) => (
              <ButtonBase
                key={a.label}
                onClick={a.onClick}
                aria-label={a.label}
                sx={{
                  bgcolor: a.primary ? BRAND : PAPER0,
                  color: a.primary ? PAPER0 : INK_HI,
                  border: a.primary ? 'none' : `1px solid ${BORDER}`,
                  boxShadow: a.primary ? '0 4px 12px rgba(31,111,235,.26)' : 'none',
                  fontSize: 13,
                  fontWeight: 700,
                  px: '22px',
                  py: '11px',
                  borderRadius: 99,
                  transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                  '&:hover': a.primary ? { opacity: 0.9 } : { borderColor: BRAND },
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                {a.label}
              </ButtonBase>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── ProfileStarterGuide ──────────────────────────────────────────────────────

/**
 * [목적] 빈 본인 프로필 하단 "이것부터 해보세요" 온보딩 카드 3종
 * [이유] 2026-09-20 profile-visual-pass — 폴리오가 0건이면 페이지 하단이 통째로 비었는데,
 *        신규 사용자에게 가장 필요한 건 "다음에 뭘 하면 되는지"다. 여백을 동선으로 바꾼다.
 * [사이드 이팩트] 각 카드는 기존 라우트로 이동만 한다(별도 상태/API 없음).
 * [수정 시 고려사항] 완료 여부 체크 표시는 넣지 않았다 — 프로필/취향 완료 상태를 판정하려면
 *                   useMe(본인 전용, bio·interests 보유)를 추가로 불러야 해서 이 컴포넌트가
 *                   데이터 의존을 갖게 된다. 필요해지면 그때 props 로 주입받을 것.
 */
const STARTER_STEPS: ReadonlyArray<{
  emoji: string;
  title: string;
  desc: string;
  to: string;
}> = [
  {
    emoji: '✍️',
    title: '프로필 채우기',
    desc: '자기소개와 사진 추가하기',
    to: '/settings/profile',
  },
  { emoji: '🗺', title: '첫 폴리오 만들기', desc: '대화 한 줄로 일정 완성하기', to: '/new-folio' },
  {
    emoji: '🧭',
    title: '취향 설정하기',
    desc: '관심 테마 고르고 추천 받기',
    to: '/settings/taste',
  },
];

function ProfileStarterGuide(): JSX.Element {
  const navigate = useNavigate();

  return (
    <Box component="section" aria-label="시작 가이드" sx={{ mt: '40px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', mb: '14px' }}>
        <Typography
          component="h2"
          sx={{
            fontSize: 18,
            fontWeight: 800,
            color: INK_HI,
            letterSpacing: '-0.4px',
            m: 0,
            flex: '0 0 auto',
          }}
        >
          이것부터 해보세요
        </Typography>
        <Box aria-hidden="true" sx={{ flex: '1 1 auto', height: '1px', bgcolor: SOFT_LINE }} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '12px',
        }}
      >
        {STARTER_STEPS.map((s) => (
          <ButtonBase
            key={s.to}
            onClick={() => navigate(s.to)}
            aria-label={`${s.title} — ${s.desc}`}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '13px',
              width: '100%',
              textAlign: 'left',
              p: '16px 17px',
              bgcolor: PAPER0,
              border: `1px solid ${BORDER}`,
              borderRadius: `${tokens.radius.l}px`,
              transition: `all ${tokens.motion.med.duration}ms ${tokens.motion.med.easing}`,
              '&:hover': {
                borderColor: BRAND,
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 22px rgba(41,38,27,.10)',
              },
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                width: 38,
                height: 38,
                borderRadius: `${tokens.radius.m}px`,
                bgcolor: PAPER2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              {s.emoji}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK_HI, mb: '3px' }}>
                {s.title}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: INK_LOW, lineHeight: 1.5 }}>
                {s.desc}
              </Typography>
            </Box>
            <Box aria-hidden="true" sx={{ color: INK_LOW, fontSize: 15, alignSelf: 'center' }}>
              ›
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}
